import { getViemClients, } from "@bracket/contractor-next";
import { assertAllowance, } from "@bracket/contractor-next/erc20";
import type { Config, } from "@wagmi/core";

import {
	bigintize,
	getContractWithABI,
	getTokenDefinition,
	isBlockHash,
} from "@bracket/chainography";

import type {
	BlockHash,
	TokenIdentifier,
	ValidChainId,
} from "@bracket/types";


export declare type MintProps = {
	amount: number|bigint;
	token: TokenIdentifier;
	minAmount?: number|bigint;
	chainId?: ValidChainId;
	config?: Config;
};



/**
 * Mints BRKTETH tokens by depositing collateral tokens.
 * @param {MintProps} props - The minting parameters
 * @param {TokenIdentifier} props.token - The collateral token to deposit
 * @param {number|bigint} props.amount - Amount to mint (in token units or WEI)
 * @param {number|bigint} props.minAmount - Optional Minimum amount to receive
 * @param {Config} [props.config] - Optional wagmi/viem client config
 * @param {ValidChainId} [props.chainId=1] - Chain ID (currently only supports 1)
 * @returns {Promise<BlockHash>} The transaction block hash
 * @throws {Error} If minting fails or chain ID is not supported
 */
export const mint = async ({
	token,
	amount,
	minAmount,
	config,
	chainId = 1,
}: MintProps): Promise<BlockHash> => {

	try {

		if(chainId !== 1)
			throw new Error("brktEth transactions are only supported on mainnet");

		const [client, wclient] = await getViemClients({ chainId, config, });
		const tok = getTokenDefinition(token);
		const [address, abi] = getContractWithABI("BrktETH");	
		const biamount = typeof amount === "bigint" ? amount : bigintize(amount, token);
		const biminAmount = typeof minAmount === "bigint" ? minAmount : bigintize(minAmount||amount, token);

		await assertAllowance({
			amount: biamount,
			token: tok,
			spender: address,
			config,
		});

		const {
			request,
			result,
		} = await client.simulateContract({
			address,
			abi,
			functionName: "mint",
			args: [
				tok.address,
				biamount,
				biamount,
			],
			account: wclient.account.address
		});

		if(result) {
			const hash = await wclient.writeContract(request);
			await client.waitForTransactionReceipt({ hash, });
			if(isBlockHash(hash)) {
				return hash;
			}
			throw new Error("did not receive a blockhash");
		}
		throw new Error("mint transaction failed");
	} catch (err) {
		throw new Error(`@bracket/chainography/brktETH:mint has failed - ${err.message}`);
	}
};
